import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { useWorkspaceLocalStorage } from "@/hooks/useWorkspaceLocalStorage";
import { isLovablePreview } from "@/lib/utils/environment";
import { withUniqueListNames } from "@/lib/utils/unique-list-names";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { useCurrentUser } from "@/lib/hooks/use-current-user";
import {
  useGetListFoldersQuery,
  useGetListFolderAssignmentsQuery,
  useCreateListFolderMutation,
  useUpdateListFolderMutation,
  useDeleteListFolderMutation,
  useMoveListToFolderMutation,
} from "@/lib/graphql/types-and-hooks";

export type ListFolderCategory = "people" | "companies" | "deals";
export type ListFolderScope = "personal" | "shared";

export interface ListFolder {
  id: string;
  name: string;
  scope: ListFolderScope;
  /** Names of custom (non-built-in) lists that belong to this folder. */
  listNames: string[];
}

const KEY_PREFIX = "list-folders";

const categoryToModelType: Record<ListFolderCategory, string> = {
  people: "CONTACT",
  companies: "ACCOUNT",
  deals: "DEAL",
};

function makeId() {
  return `f_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Folder organization for saved lists on People / Companies / Deals pages.
 *
 * Production: backed by the list-folders GraphQL API (list_folder table +
 * `folderId` on List). Folder membership is workspace truth keyed by list id;
 * a folder's scope maps to its `public` flag — shared folders are visible to
 * the whole workspace, personal folders only to their owner. Lists sitting in
 * someone else's personal folder surface as uncategorized here, by design.
 *
 * Preview / fallback (backend not deployed yet, or queries erroring): the
 * original per-workspace localStorage implementation, where the list NAME is
 * the key (the same name==id convention AddToListDialog uses in preview).
 *
 * The render layer (SectionTitleDropdown) still works with list names;
 * `getListId` translates names to ids for the mutations.
 */
export function useListFolders(category: ListFolderCategory) {
  const preview = isLovablePreview();
  const workspaceId = useWorkspaceId();
  const { currentUser } = useCurrentUser();
  const modelType = categoryToModelType[category];

  // ── Local (preview / fallback) state — hooks always run, per rules-of-hooks.
  const [localFolders, setLocalFolders] = useWorkspaceLocalStorage<ListFolder[]>(
    `${KEY_PREFIX}:${category}`,
    [],
  );

  // Per-list scope tracking (independent of folder membership) — preview-only
  // remnant; production derives scope from List.public (see SectionTitleDropdown).
  const [listScopes, setListScopes] = useWorkspaceLocalStorage<Record<string, ListFolderScope>>(
    `${KEY_PREFIX}-list-scopes:${category}`,
    {},
  );

  // ── Backend state.
  const skip = preview || !workspaceId;
  const { data: foldersData, error: foldersError } = useGetListFoldersQuery({
    variables: { workspaceId: workspaceId || "", modelType },
    skip,
    fetchPolicy: "cache-first",
  });
  const { data: assignmentsData, error: assignmentsError } = useGetListFolderAssignmentsQuery({
    variables: { filter: { workspaceId: workspaceId || undefined, modelType, userId: currentUser?.id || undefined } },
    skip,
    fetchPolicy: "cache-first",
  });

  // Backend not deployed yet (fields missing) or transient failure → behave
  // exactly like the historical localStorage implementation.
  const fallback = skip || !!foldersError || !!assignmentsError;

  const [createListFolderMutation] = useCreateListFolderMutation();
  const [updateListFolderMutation] = useUpdateListFolderMutation();
  const [deleteListFolderMutation] = useDeleteListFolderMutation();
  const [moveListToFolderMutation] = useMoveListToFolderMutation();

  // Backend enforces owner-only rules (e.g. only the owner may change a
  // folder's visibility) — surface rejections instead of leaving an unhandled
  // promise and a UI that silently didn't change.
  const toastError = (fallbackMessage: string) => (err: unknown) => {
    toast.error(err instanceof Error ? err.message : fallbackMessage);
  };

  // (list id, name, folderId) tuples backing name→id translation and grouping.
  // Names are de-duplicated the same way the pages do, so they match the picker.
  const listTuples = useMemo(() => {
    if (fallback) return [];
    return withUniqueListNames((assignmentsData?.userLists?.edges ?? [])
      .map(e => e?.node?.list)
      .filter((l): l is NonNullable<typeof l> => !!l))
      .map(l => ({ id: String(l.id), name: l.name || "", folderId: l.folderId != null ? String(l.folderId) : null }));
  }, [fallback, assignmentsData]);

  const backendFolders = useMemo<ListFolder[]>(() => {
    if (fallback) return [];
    const byId = new Map<string, ListFolder>();
    for (const f of foldersData?.listFolders ?? []) {
      byId.set(String(f.id), {
        id: String(f.id),
        name: f.name,
        scope: f.public ? "shared" : "personal",
        listNames: [],
      });
    }
    for (const t of listTuples) {
      if (!t.folderId) continue;
      const folder = byId.get(t.folderId);
      // Lists in a folder the caller can't see (someone else's personal
      // folder) intentionally fall out as uncategorized.
      if (folder && t.name) folder.listNames.push(t.name);
    }
    return [...byId.values()];
  }, [fallback, foldersData, listTuples]);

  const folders = useMemo(() => {
    const src = fallback ? localFolders : backendFolders;
    return [...src].sort((a, b) => a.name.localeCompare(b.name));
  }, [fallback, localFolders, backendFolders]);

  const folderByListName = useMemo(() => {
    const map = new Map<string, ListFolder>();
    for (const f of folders) for (const n of f.listNames) map.set(n, f);
    return map;
  }, [folders]);

  /** Backend list id for a list name; in preview/fallback the name IS the id. */
  const getListId = useCallback((listName: string): string | null => {
    if (fallback) return listName;
    return listTuples.find(t => t.name === listName)?.id ?? null;
  }, [fallback, listTuples]);

  const setListScope = useCallback((listName: string, scope: ListFolderScope) => {
    setListScopes(prev => ({ ...prev, [listName]: scope }));
  }, [setListScopes]);

  const getListScope = useCallback((listName: string): ListFolderScope => {
    if (listScopes[listName]) return listScopes[listName];
    const f = folderByListName.get(listName);
    if (f) return f.scope;
    return "shared";
  }, [listScopes, folderByListName]);

  const createFolder = useCallback(
    async (name: string, scope: ListFolderScope): Promise<ListFolder | null> => {
      if (fallback) {
        const folder: ListFolder = { id: makeId(), name: name.trim(), scope, listNames: [] };
        setLocalFolders(prev => [...prev, folder]);
        return folder;
      }
      let result;
      try {
        result = await createListFolderMutation({
          variables: {
            input: {
              workspaceId: workspaceId || "",
              ownerId: currentUser?.id || "",
              name: name.trim(),
              modelType,
              public: scope === "shared",
            },
          },
          refetchQueries: ["getListFolders"],
        });
      } catch (err) {
        toastError("Failed to create folder")(err);
        return null;
      }
      const created = result.data?.createListFolder?.listFolder;
      if (!created) return null;
      return {
        id: String(created.id),
        name: created.name,
        scope: created.public ? "shared" : "personal",
        listNames: [],
      };
    },
    [fallback, setLocalFolders, createListFolderMutation, workspaceId, currentUser?.id, modelType],
  );

  const renameFolder = useCallback((id: string, name: string) => {
    if (fallback) {
      setLocalFolders(prev => prev.map(f => (f.id === id ? { ...f, name: name.trim() } : f)));
      return;
    }
    // Response carries ListFolderFields → cache normalization updates the row.
    updateListFolderMutation({
      variables: { input: { id, workspaceId: workspaceId || "", name: name.trim() } },
    }).catch(toastError("Failed to rename folder"));
  }, [fallback, setLocalFolders, updateListFolderMutation, workspaceId]);

  const setFolderScope = useCallback((id: string, scope: ListFolderScope) => {
    if (fallback) {
      setLocalFolders(prev => prev.map(f => (f.id === id ? { ...f, scope } : f)));
      return;
    }
    updateListFolderMutation({
      variables: { input: { id, workspaceId: workspaceId || "", public: scope === "shared" } },
    }).catch(toastError("Failed to update folder visibility"));
  }, [fallback, setLocalFolders, updateListFolderMutation, workspaceId]);

  const deleteFolder = useCallback((id: string) => {
    if (fallback) {
      setLocalFolders(prev => prev.filter(f => f.id !== id));
      return;
    }
    // The backend clears folder_id on member lists; the assignments refetch
    // mirrors that client-side (deleteListFolder only returns `ok`, so there
    // is no payload to normalize from).
    deleteListFolderMutation({
      variables: { input: { id, workspaceId: workspaceId || "" } },
      refetchQueries: ["getListFolders", "getListFolderAssignments"],
    }).catch(toastError("Failed to delete folder"));
  }, [fallback, setLocalFolders, deleteListFolderMutation, workspaceId]);

  /**
   * Move a list into a folder (or to the top level when folderId is null).
   * Production expects the backend list id (use getListId to translate a
   * name); preview/fallback expects the list name.
   */
  const moveListToFolder = useCallback((listKey: string, folderId: string | null) => {
    if (fallback) {
      setLocalFolders(prev => prev.map(f => {
        const has = f.listNames.includes(listKey);
        if (f.id === folderId) {
          return has ? f : { ...f, listNames: [...f.listNames, listKey] };
        }
        return has ? { ...f, listNames: f.listNames.filter(n => n !== listKey) } : f;
      }));
      return;
    }
    // updateList's response (id + folderId) updates the assignments query via
    // cache normalization — no refetch.
    moveListToFolderMutation({
      variables: { input: { id: listKey, folderId } },
    }).catch(toastError("Failed to move list"));
  }, [fallback, setLocalFolders, moveListToFolderMutation]);

  /** Rename a list in place inside whichever folder holds it, if any.
   *  Production no-op: membership is keyed by list id, renames don't touch it. */
  const renameListInFolders = useCallback((oldName: string, newName: string) => {
    if (!fallback) return;
    setLocalFolders(prev => prev.map(f => (
      f.listNames.includes(oldName)
        ? { ...f, listNames: f.listNames.map(n => (n === oldName ? newName : n)) }
        : f
    )));
    setListScopes(prev => {
      if (!(oldName in prev)) return prev;
      const { [oldName]: scope, ...rest } = prev;
      return { ...rest, [newName]: scope };
    });
  }, [fallback, setLocalFolders, setListScopes]);

  /** Remove a list from every folder (e.g., after the list itself is deleted).
   *  Production no-op: hiding/deleting a list already removes it from view, and
   *  hard deletes clear folder_id via the FK. */
  const removeListFromFolders = useCallback((listName: string) => {
    if (!fallback) return;
    setLocalFolders(prev => prev.map(f => (
      f.listNames.includes(listName)
        ? { ...f, listNames: f.listNames.filter(n => n !== listName) }
        : f
    )));
    setListScopes(prev => {
      if (!(listName in prev)) return prev;
      const next = { ...prev };
      delete next[listName];
      return next;
    });
  }, [fallback, setLocalFolders, setListScopes]);

  return {
    folders,
    folderByListName,
    getListId,
    createFolder,
    renameFolder,
    setFolderScope,
    deleteFolder,
    moveListToFolder,
    renameListInFolders,
    removeListFromFolders,
    setListScope,
    getListScope,
  };
}
